import type { IClientRouting } from '../routing/i-client-routing';
import type { IClientStorage } from './i-client-storage';
import type { ICurrentUser } from './i-current-user';
import type { IPopupService } from '../services/i-popup-service';
import type { LocalizedString } from '../dto/localized-string';

export type Newable<TInstance = unknown, TArgs extends unknown[] = unknown[]> = new (...args: TArgs) => TInstance;

export type ServiceIdentifier<TInstance = unknown> = string | symbol | Newable<TInstance> | Function;

export interface GetOptions {
	/**
	 * Reserved for future use (optional resolution, named bindings, etc.).
	 */
	readonly optional?: boolean;
}

export function stringifyServiceIdentifier(serviceIdentifier: ServiceIdentifier): string {
	if (typeof serviceIdentifier === 'string') return serviceIdentifier;
	if (typeof serviceIdentifier === 'symbol') return serviceIdentifier.toString();
	if (typeof serviceIdentifier === 'function') return serviceIdentifier.name || '[anonymous function]';
	return String(serviceIdentifier);
}

/**
 * Computed value accessor
 */
export interface IComputed<T> {
	readonly value: T;
}

/**
 * Computed value with getter
 */
export interface IComputedValue<T> extends IComputed<T> {}

/**
 * Watch callback function
 */
export type WatchCallback<T> = (newValue: T, oldValue: T) => void;

/**
 * Watch stop handle
 */
export type WatchStopHandle = () => void;

/**
 * Service accessor function (kept for compatibility with older ViewModels).
 */
export type ServiceAccessor = <T>(serviceIdentifier: ServiceIdentifier<T>, options?: GetOptions) => T;

/**
 * Environment interface for framework-agnostic reactive utilities
 * This abstracts all framework-specific APIs including Vue's reactive, computed, watch, and services
 */
export interface IEnv {
	/**
	 * Current user abstraction (auth state lives in UI layer).
	 */
	readonly currentUser: ICurrentUser;

	/**
	 * Routing abstraction
	 */
	readonly routing: IClientRouting;

	/**
	 * Client-side storage abstraction (localStorage, etc.)
	 */
	readonly clientStorage: IClientStorage;

	/**
	 * Popup service abstraction (host-controlled popups)
	 */
	readonly popupService: IPopupService;

	/**
	 * Creates a reactive object
	 * @param obj - The object to make reactive
	 * @returns The reactive version of the object
	 */
	reactive<T extends object>(obj: T): T;

	/**
	 * Creates a computed value
	 * @param getter - Function that computes the value
	 * @returns Computed value accessor
	 */
	computed<T>(getter: () => T): IComputedValue<T>;

	/**
	 * Watches a reactive source for changes
	 * @param source - Getter function that returns the value to watch
	 * @param callback - Function called when the value changes
	 * @returns Function to stop watching
	 */
	watch<T>(source: () => T, callback: WatchCallback<T>): WatchStopHandle;

	/**
	 * Watches multiple reactive sources for changes
	 * @param sources - Array of getter functions
	 * @param callback - Function called when any value changes
	 * @returns Function to stop watching
	 */
	watchMultiple<T extends readonly unknown[]>(
		sources: readonly [...{ [K in keyof T]: () => T[K] }],
		callback: (newValues: T, oldValues: T) => void
	): WatchStopHandle;

	/**
	 * Get a service instance by identifier (string/symbol/class).
	 * This is intentionally compatible with InversifyJS identifiers.
	 * @param serviceIdentifier - Service identifier
	 * @returns Service instance
	 */
	get<T>(serviceIdentifier: ServiceIdentifier<T>, options?: GetOptions): T;

	/**
	 * Format a date for display
	 * @param date - Date string or Date object
	 * @returns Formatted date string
	 */
	formatDate(date: string | Date | null | undefined): string;

	/**
	 * Show an alert message
	 * @param message - Message to display
	 */
	showAlert(message: string): void;

	/**
	 * Show a confirmation dialog
	 * @param message - Message to display
	 * @returns Promise<boolean> - true if confirmed, false if cancelled
	 */
	showConfirm(message: string): Promise<boolean>;

	/**
	 * Run a callback on next tick (after DOM updates)
	 * @param callback - Function to run
	 */
	nextTick(callback: () => void): void;

	/**
	 * Log a message (for debugging)
	 * @param message - Message to log
	 * @param data - Optional data to log
	 */
	log(message: string, data?: unknown): void;

	/**
	 * Convert a LocalizedString into displayable text in the current UI environment
	 * (e.g. via i18n), or return a safe fallback.
	 */
	localizedStringToText(value: LocalizedString | null | undefined): string;
}

